import { useState, useEffect, useMemo } from "react";
import { useIntegrationProviders } from "@/features/integrations_provider/hooks/useIntegrationProviders";
import { useClientIntegrations } from "../hooks/useClientIntegrations";
import type IntegrationProvider from "@/features/integrations_provider/models/integrationProvider";
import type UserIntegrationToken from "../models/UserIntegrationToken";
import { ClientIntegrationsGrid } from "../components/client_integrations_grid";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { confirmDialog } from "@/shared/components/dialog";
import GlassBackground from "@/shared/components/glassBackground";

export default function ClientIntegrationsPage() {
  const [providers, setProviders] = useState<IntegrationProvider[]>([]);
  const [connections, setConnections] = useState<UserIntegrationToken[]>([]);
  const [oauthStatus, setOauthStatus] = useState<
    "idle" | "processing" | "success" | "error"
  >("idle");
  const [oauthErrorMessage, setOauthErrorMessage] = useState<string>("");
  const [connectedProviderId, setConnectedProviderId] = useState<string | null>(
    null,
  );
  const [connectedProviderName, setConnectedProviderName] =
    useState<string>("");

  const {
    loading: providersLoading,
    error: providersError,
    getAll,
  } = useIntegrationProviders();

  const {
    loading: connectionsLoading,
    error: connectionsError,
    getMyConnections,
    disconnect,
    connect,
  } = useClientIntegrations();

  // Detect return from OAuth flow via query params or sessionStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get("oauth");
    const message = params.get("message");

    if (oauth === "success") {
      setOauthStatus("success");
      setOauthErrorMessage("");
      setConnectedProviderId(params.get("providerId"));
      setConnectedProviderName(params.get("providerName") || "");
      sessionStorage.removeItem("oauthPending");
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      // Auto-clear notification after 8s (longer so user sees the provider name)
      setTimeout(() => {
        setOauthStatus("idle");
        setConnectedProviderId(null);
        setConnectedProviderName("");
      }, 8000);
    } else if (oauth === "error") {
      setOauthStatus("error");
      setOauthErrorMessage(message ? decodeURIComponent(message) : "");
      sessionStorage.removeItem("oauthPending");
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setOauthStatus("idle"), 7000);
    } else {
      const pending = sessionStorage.getItem("oauthPending");
      if (pending) {
        setOauthStatus("processing");
        sessionStorage.removeItem("oauthPending");
      }
    }
  }, []);

  // Fetch providers and connections
  useEffect(() => {
    const fetchData = async () => {
      const [providersPage, connectionsList] = await Promise.all([
        getAll({ pageNumber: 1, pageSize: 100 }),
        getMyConnections(),
      ]);
      if (providersPage) setProviders(providersPage.items);
      if (connectionsList) setConnections(connectionsList);
    };
    fetchData();
  }, [getAll, getMyConnections]);

  // Refetch connections after OAuth success so the grid updates
  useEffect(() => {
    if (oauthStatus === "success") {
      getMyConnections().then((list) => {
        if (list) setConnections(list);
      });
    }
  }, [oauthStatus, getMyConnections]);

  const connectedIds = useMemo(() => {
    return new Set(
      connections
        .filter((c) => c.status === "Authorized")
        .map((c) => c.integrationProviderId),
    );
  }, [connections]);

  const connectedCount = connectedIds.size;
  const totalCount = providers.length;

  const handleConnect = async (providerId: string) => {
    sessionStorage.setItem("oauthPending", "true");
    const returnUrl = `${window.location.origin}/admin/client_integrations`;
    await connect(providerId, returnUrl);
  };

  const handleDisconnect = async (providerId: string) => {
    const confirmed = await confirmDialog({
      title: "Desconectar integración",
      message: "¿Desconectar esta integración? Se perderá el acceso hasta que vuelvas a conectar.",
      confirmLabel: "Sí, desconectar",
      cancelLabel: "Cancelar",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await disconnect(providerId);
      setConnections((prev) =>
        prev.filter((c) => c.integrationProviderId !== providerId),
      );
    } catch {
      // error toast handled by useAsync
    }
  };

  const loading = providersLoading || connectionsLoading;
  const error = providersError || connectionsError;

  return (
    <GlassBackground>
      <div className="relative flex-1 overflow-auto p-8">
        <div className="max-w-full mx-auto">
          {/* Header */}
          {oauthStatus !== "idle" && (
            <div
              className={`mb-6 p-4 rounded-xl border backdrop-blur-md flex items-center gap-3 ${oauthStatus === "processing"
                ? "bg-blue-500/5 border-blue-500/20 text-blue-700"
                : oauthStatus === "success"
                  ? "bg-green-500/5 border-green-500/20 text-green-700"
                  : "bg-red-500/5 border-red-500/20 text-red-700"
                }`}
            >
              {oauthStatus === "processing" && (
                <Loader2 className="size-5 animate-spin" />
              )}
              {oauthStatus === "success" && <CheckCircle className="size-5" />}
              {oauthStatus === "error" && <AlertCircle className="size-5" />}
              <p className="text-sm font-semibold">
                {oauthStatus === "processing" &&
                  "Procesando conexión, por favor espera..."}
                {oauthStatus === "success" &&
                  (connectedProviderName
                    ? `¡${connectedProviderName} conectado exitosamente!`
                    : "¡Conexión exitosa! Tu integración ha sido vinculada.")}
                {oauthStatus === "error" &&
                  (oauthErrorMessage ||
                    "No se pudo completar la conexión. Inténtalo de nuevo.")}
              </p>
            </div>
          )}

          <div className="mb-8">
            <h2 className="text-[40px] font-bold tracking-tight text-foreground">
              Mis Integraciones
            </h2>
            <div className="flex items-center gap-3 mt-2">
              <p className="text-foreground/50 text-lg font-medium">
                Conecta y gestiona tus cuentas de proveedores externos.
              </p>
              {totalCount > 0 && (
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${connectedCount > 0
                    ? "bg-green-500/10 text-green-700 border border-green-500/20"
                    : "bg-gray-500/10 text-gray-600 border border-gray-500/20"
                    }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${connectedCount > 0 ? "bg-green-500" : "bg-gray-400"
                      }`}
                  />
                  {connectedCount} / {totalCount} activas
                </span>
              )}
            </div>
          </div>

          <ClientIntegrationsGrid
            providers={providers}
            connectedIds={connectedIds}
            highlightedId={connectedProviderId ?? undefined}
            loading={loading}
            error={error}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
          />
        </div>
      </div>
    </GlassBackground>
  );
}
